"use client"

import Link from "next/link"
import { useCallback, useId, useState } from "react"
import type { UserView } from "../lib/user-view.ts"
import { FormattedPointAmount } from "./point-amount.tsx"
import { useManagedDialog } from "./use-managed-dialog.ts"

export type HistoryEntry = Extract<UserView, { readonly kind: "history" }>["entries"][number]

const dateTimeFormatter = new Intl.DateTimeFormat("ko-KR", { dateStyle: "long", timeStyle: "short" })

export const historyDisplayLabel = (label: string, sourceMediaName?: string | null): string => (label === "포인트 전환" && sourceMediaName ? `${sourceMediaName} 포인트 전환` : label === "상품 사용" ? "상품 교환 완료" : label)

export const userVisibleHistoryEntries = <Entry extends HistoryEntry>(entries: readonly Entry[]): Entry[] => entries.filter(({ label }) => label !== "상품 교환 요청")

const historyExplanation = (entry: HistoryEntry): { readonly detail: string; readonly summary: string } => {
  if (entry.label === "상품 사용") {
    return {
      detail: "교환 요청 때 임시 보류했던 포인트를 최종 차감한 기록입니다. 요청과 완료 과정에서 포인트가 두 번 차감된 것이 아닙니다.",
      summary: "상품 교환이 완료되었어요.",
    }
  }
  if (entry.amount.trim().startsWith("+")) return { detail: "처리가 끝나 사용 가능한 CashHub 잔액에 반영되었습니다.", summary: "잔액이 정상적으로 추가되었어요." }
  if (entry.label === "잔액 소멸") return { detail: "사용기한이 지난 잔액이 정책에 따라 소멸된 기록입니다.", summary: "사용기한이 끝난 잔액이에요." }
  return { detail: "완료된 처리 결과가 CashHub 잔액에 반영되었습니다.", summary: "잔액 변동이 완료되었어요." }
}

export const HistoryDetailSheet = ({ entry, onClose }: { readonly entry: HistoryEntry | null; readonly onClose: () => void }) => {
  const headingId = useId()
  const [copied, setCopied] = useState(false)
  const handleClose = useCallback(() => {
    setCopied(false)
    onClose()
  }, [onClose])
  const { cancel, dialogRef, onDialogClose } = useManagedDialog(entry !== null, handleClose)

  const explanation = entry === null ? null : historyExplanation(entry)

  return (
    <dialog
      aria-labelledby={headingId}
      className="history-detail-dialog"
      data-history-detail-sheet
      onCancel={(event) => {
        event.preventDefault()
        cancel()
      }}
      onClose={onDialogClose}
      ref={dialogRef}
    >
      {entry === null || explanation === null ? null : (
        <div className="history-detail-content">
          <header className="history-detail-heading">
            <div>
              <span className="history-detail-kicker">이용내역 상세</span>
              <h2 id={headingId}>{historyDisplayLabel(entry.label, entry.sourceMediaName)}</h2>
            </div>
            <button aria-label="상세 내역 닫기" className="history-detail-close" onClick={cancel} type="button">
              ×
            </button>
          </header>
          <strong className="history-detail-amount" data-amount-direction={entry.amount.trim().startsWith("-") ? "debit" : entry.amount.trim().startsWith("+") ? "credit" : "neutral"}>
            <FormattedPointAmount value={entry.amount} />
          </strong>
          <dl className="history-detail-list">
            {entry.label === "포인트 전환" ? (
              <div>
                <dt>전환 출처</dt>
                <dd>{entry.sourceMediaName ?? "출처 정보 없음"}</dd>
              </div>
            ) : null}
            <div>
              <dt>처리 상태</dt>
              <dd>완료</dd>
            </div>
            <div>
              <dt>처리 시각</dt>
              <dd>{dateTimeFormatter.format(new Date(entry.occurredAt))}</dd>
            </div>
            <div className="history-detail-reference-row">
              <dt>확인 번호</dt>
              <dd className="history-detail-reference">
                <span className="history-detail-reference-value">{entry.id}</span>
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(entry.id)
                    setCopied(true)
                  }}
                  type="button"
                >
                  {copied ? "복사됨" : "복사"}
                </button>
              </dd>
            </div>
          </dl>
          {entry.label === "상품 사용" ? (
            <Link className="history-order-link" href="/orders">
              주문 내역에서 상품 확인
            </Link>
          ) : null}
          <section className="history-detail-note">
            <strong>{explanation.summary}</strong>
            <p>{explanation.detail}</p>
          </section>
        </div>
      )}
    </dialog>
  )
}
